// src/api/frontends.ts
/**
 * Frontends — what personalization is delivered TO (ADR-0182).
 *
 * A frontend is one delivery surface with its own personalization storage.
 * There are two kinds, and the difference the customer feels is who owns that
 * storage:
 *
 * - `SHOPIFY_STOREFRONT` — a Shopify store. Verity owns the key-value
 *   namespace and runs the worker that serves personalized content, so the
 *   row carries a shop domain and a delivery endpoint, and deleting it
 *   permanently removes the visitor data Verity holds for that store.
 * - `CLOUDFLARE_HOSTED` — the customer's own site, backed by the customer's
 *   own Cloudflare KV namespace. Verity writes visitor profiles into that
 *   namespace and runs no worker for it, so there is no delivery endpoint and
 *   deleting the frontend removes only Verity's configuration. The customer's
 *   namespace and its contents stay exactly as they are.
 * - `PIXEL_SITE` — a plain website that only has the Verity Pixel ("Website"
 *   in the UI). No namespace and no worker; it exists so on-site messages
 *   (Onsite activation, plan 2026-10-07) have a frontend to belong to. It is
 *   created together with its pixel-site link in one transaction.
 *
 * Accounts historically had exactly one frontend; these calls make N explicit.
 * The backend enforces the contract (account scoping, the no-default rule, the
 * Plus-org boundary on a Shopify create, the live Cloudflare check on a
 * Cloudflare-hosted create).
 */
import { apiJson } from "@/api/client";

export type FrontendKind = "SHOPIFY_STOREFRONT" | "CLOUDFLARE_HOSTED" | "PIXEL_SITE";

export interface FrontendDTO {
  id: string; // fnt_…
  kind: FrontendKind;
  name: string;
  shop_domain: string | null;
  canonical_id_type: string;
  /** False marks a customer-owned (bring-your-own) Cloudflare namespace: it is
   * never provisioned and never deleted by Verity. */
  namespace_managed: boolean;
  provisioned: boolean;
  worker_url: string | null;
}

/** The visitor identifiers a Cloudflare-hosted frontend can key profiles on.
 * Mirrors the backend's allow-list (`db/models/frontends.py`); anything else
 * is a 422. */
export const CANONICAL_ID_TYPES = [
  "cookie_id",
  "account_id",
  "device_id",
  "email_hash",
  "verity_id",
] as const;

export type CanonicalIdType = (typeof CANONICAL_ID_TYPES)[number];

export async function listFrontends(): Promise<FrontendDTO[]> {
  // apiJson auto-unwraps { data: ... }.
  const res = await apiJson<{ frontends: FrontendDTO[] }>("/frontends", {
    method: "GET",
  });
  return res.frontends ?? [];
}

/** One Cloudflare KV destination a new Cloudflare-hosted frontend may adopt.
 *
 * Hand-written against the backend serializer (`_serialize_candidate` in
 * `api/routes/frontend_routes.py`), not the generated OpenAPI types: the route
 * is annotated `-> Dict`, so the spec says nothing about this shape.
 *
 * The three ids are returned exactly as `createHeadlessFrontend` takes them —
 * copy them from one candidate and the triple can never be mismatched. */
export interface FrontendDestinationCandidateDTO {
  /** Public id, `dst_…`. Identity of the choice; not sent on create. */
  destination_id: string;
  name: string;
  /** The customer's Cloudflare KV namespace (32-hex). */
  namespace_id: string;
  authorization_id: string;
  authorization_name: string;
  data_source_id: string;
  data_source_name: string;
  /** How this data source identifies visitors, when it says so. Null means the
   * customer still picks — the backend skips its drift check in that case. */
  identity_id_type: CanonicalIdType | null;
  /** Enabled segments already routed to this destination — the signal that
   * this is the row carrying the account's profiles. */
  linked_segment_count: number;
  schedule_type: string;
  /** A paused destination stays adoptable; the pause describes the sync, not
   * the binding. Shown so the customer is not surprised by it. */
  is_active: boolean;
}

/**
 * The KV destinations this account already has that a new Cloudflare-hosted
 * frontend can be attached to.
 *
 * Reuse is the point: adopting one makes the frontend deliver into the SAME
 * namespace and the SAME per-visitor document that already carries the
 * account's segments, instead of standing up a second store no segment is
 * routed to. Admin-only, like every other call in this module.
 *
 * An empty list is a normal answer — it means "no candidates, take the
 * set-up-a-new-namespace path".
 */
export async function listFrontendDestinationCandidates(): Promise<
  FrontendDestinationCandidateDTO[]
> {
  // apiJson auto-unwraps { data: ... }.
  const res = await apiJson<{ destinations: FrontendDestinationCandidateDTO[] }>(
    "/frontends/destination-candidates",
    { method: "GET" }
  );
  return res.destinations ?? [];
}

export async function createFrontend(input: {
  shop_domain: string;
  name?: string;
}): Promise<FrontendDTO> {
  return apiJson<FrontendDTO>("/frontends", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Add a Cloudflare-hosted frontend: the customer's own site on the customer's
 * own KV namespace.
 *
 * A separate call rather than a wider `createFrontend` on purpose. The create
 * endpoint forbids unknown fields AND refuses any field that belongs to the
 * other kind, so a body that mixes `shop_domain` with a namespace is a 422 the
 * user has to decipher. Two functions, two shapes, neither able to send the
 * other's fields.
 *
 * The backend checks the namespace live before it writes anything: it confirms
 * the namespace exists and that the authorization's token may write to it, and
 * it confirms the data source identifies visitors the way this frontend says
 * it does. Both refusals come back as a message written for the customer, so
 * show it as it is.
 */
export async function createHeadlessFrontend(input: {
  name: string;
  authorization_id: string;
  namespace_id: string;
  data_source_id: string;
  /** Omitted means the backend default, `cookie_id`. */
  canonical_id_type?: CanonicalIdType;
}): Promise<FrontendDTO> {
  return apiJson<FrontendDTO>("/frontends", {
    method: "POST",
    body: JSON.stringify({ kind: "CLOUDFLARE_HOSTED", ...input }),
  });
}

/**
 * Add a website (`PIXEL_SITE`): a plain site that only has the Verity Pixel,
 * so it can show on-site messages. The frontend and its link to the pixel site
 * are created in one transaction. Behind the `onsite_messages` flag (404
 * without it); 409 when the pixel site already belongs to a storefront or
 * website. Refusals are customer-readable — show them as they are.
 */
export async function createWebsiteFrontend(input: {
  /** The pixel site (`pxs_…`) this website is. */
  pixel_site_id: string;
  /** Omitted = the pixel site's first allowed host. */
  name?: string;
}): Promise<FrontendDTO> {
  return apiJson<FrontendDTO>("/frontends", {
    method: "POST",
    body: JSON.stringify({ kind: "PIXEL_SITE", ...input }),
  });
}

/** Deletion permanently removes the personalization data Verity holds for the
 * frontend — the confirm literal names that cost and the backend refuses
 * without it. For a customer-owned Cloudflare namespace only Verity's
 * configuration goes; the namespace itself is untouched. */
export async function deleteFrontend(publicId: string): Promise<void> {
  await apiJson(`/frontends/${encodeURIComponent(publicId)}?confirm=delete-visitor-data`, {
    method: "DELETE",
  });
}
